import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { localizeServerError } from '@trello/legacy-i18n';
import { Button } from '@trello/nachos/button';
import { showFlag } from '@trello/nachos/experimental-flags';

import { useAddReactivatedMemberToWorkspaceMutation } from './AddReactivatedMemberToWorkspaceMutation.generated';
import { useReactivateMemberForWorkspaceMutation } from './ReactivateMemberForWorkspaceMutation.generated';

import styles from './WorkspaceMemberReactivateButton.less';

export const WorkspaceMemberReactivateButton: FunctionComponent<{
  workspaceId: string;
  memberId: string;
}> = ({ workspaceId, memberId }) => {
  const [reactivateMemberForWorkspace] =
    useReactivateMemberForWorkspaceMutation();
  const [addReactivatedMemberToWorkspace] =
    useAddReactivatedMemberToWorkspaceMutation();

  const handleClick = useCallback(async () => {
    const source = 'workspaceMembersScreen';
    const taskName = 'reactivate-member/organization';
    const traceId = Analytics.startTask({ taskName, source });

    Analytics.sendClickedButtonEvent({
      buttonName: 'reactivateMemberButton',
      source,
      containers: {
        workspace: {
          id: workspaceId,
        },
      },
      attributes: {
        taskId: traceId,
      },
    });

    try {
      if (workspaceId) {
        await reactivateMemberForWorkspace({
          variables: { workspaceId, memberId },
        });
        await addReactivatedMemberToWorkspace({
          variables: { workspaceId, user: { id: memberId }, traceId },
        });

        Analytics.sendTrackEvent({
          action: 'reactivated',
          actionSubject: 'member',
          actionSubjectId: memberId,
          source,
          containers: {
            workspace: {
              id: workspaceId,
            },
          },
          attributes: {
            taskId: traceId,
          },
        });
        Analytics.taskSucceeded({
          taskName,
          traceId,
          source,
        });
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      showFlag({
        id: 'organization-members',
        title: localizeServerError(error.message),
        appearance: 'error',
        isAutoDismiss: true,
        msTimeout: 3000,
      });
      Analytics.sendOperationalEvent({
        action: 'errored',
        actionSubject: 'reactivateMember',
        source,
        containers: {
          workspace: {
            id: workspaceId,
          },
        },
        attributes: {
          errMsg: 'Error: ' + error.message,
          taskId: traceId,
        },
      });
      Analytics.taskFailed({
        taskName,
        traceId,
        source,
        error,
      });
    }
  }, [
    reactivateMemberForWorkspace,
    addReactivatedMemberToWorkspace,
    workspaceId,
    memberId,
  ]);

  return (
    <Button onClick={handleClick} className={styles.button}>
      <FormattedMessage
        id="templates.member_list_item_detail.reactivate"
        description="Reactivate member"
        defaultMessage="Reactivate"
      />
    </Button>
  );
};
